import {announce} from '@github-ui/aria-live'
import {
  chatHistoryInfiniteQueryKey,
  chatHistoryQueryKeyPrefix,
  chatSessionQueryKey,
  chatSidebarQueryKey,
  getTaskTimestamp,
  getTaskTitle,
  useChatHistoryInfiniteQuery,
} from '@github-ui/copilot-chat-app/runtime/chat-history'
import type {SidebarChat} from '@github-ui/agent-sessions/types/sidebar-chat'
import {canPrepareSandboxChat} from '@github-ui/one-copilot-composer/shared'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {useQueryClient} from '@github-ui/react-query'

const CHATS_PAGE_SIZE = 25

export interface AgentsChats {
  chats: readonly SidebarChat[]
  isLoadingChats: boolean
  isErrorChats: boolean
  /** True while older chats remain on the server beyond the pages already loaded. */
  hasMoreChats?: boolean
  isLoadingMoreChats?: boolean
  /** True when fetching an older page failed; the pages already loaded remain usable. */
  hasLoadMoreError?: boolean
  loadMoreChats?: () => void
  apiURL?: string
  onTaskStateChange?: (taskId: string) => void
}

export function isQuickChatEntryPointEnabled({
  quickChatEnabled,
  taskDetailEnabled,
}: {
  quickChatEnabled: boolean
  taskDetailEnabled: boolean
}): boolean {
  return quickChatEnabled && taskDetailEnabled
}

export function isChatSidebarEnabled({
  agentsEnabled,
  quickChatEnabled,
  taskDetailEnabled,
  apiURL,
}: {
  agentsEnabled: boolean
  quickChatEnabled: boolean
  taskDetailEnabled: boolean
  apiURL?: string
}): boolean {
  return agentsEnabled && isQuickChatEntryPointEnabled({quickChatEnabled, taskDetailEnabled}) && Boolean(apiURL)
}

export function isAgentsChatCreationEnabled({
  quickChatEnabled,
  taskDetailEnabled,
  showSandboxOption,
  hasSpecifiedRepository,
  scopeLogin,
}: {
  quickChatEnabled: boolean
  taskDetailEnabled: boolean
  showSandboxOption: boolean
  hasSpecifiedRepository: boolean
  scopeLogin?: string
}): boolean {
  return (
    isQuickChatEntryPointEnabled({quickChatEnabled, taskDetailEnabled}) &&
    canPrepareSandboxChat({
      chatEnabled: true,
      hasEnvironmentSubmit: showSandboxOption,
      hideRepoSelector: false,
      showSandboxOption,
      hasSpecifiedRepository,
      scopeLogin,
    })
  )
}

export function useAgentsChats({
  apiURL,
  enabled,
  announceResults = true,
}: {
  apiURL?: string
  enabled: boolean
  announceResults?: boolean
}): AgentsChats {
  const queryClient = useQueryClient()
  const queryEnabled = enabled && Boolean(apiURL)
  const {query, tasks, hasLoadError, hasFetchNextPageError, hasNextPage, isFetchingNextPage, fetchNextPage} =
    useChatHistoryInfiniteQuery({
      apiURL,
      queryKey: chatHistoryInfiniteQueryKey(apiURL, CHATS_PAGE_SIZE),
      pageSize: CHATS_PAGE_SIZE,
      enabled: queryEnabled,
    })

  const loadMoreChats = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return
    void fetchNextPage()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const onTaskStateChange = useCallback(
    (taskId: string) => {
      void queryClient.invalidateQueries({queryKey: chatHistoryInfiniteQueryKey(apiURL, CHATS_PAGE_SIZE)})
      void queryClient.invalidateQueries({queryKey: chatHistoryQueryKeyPrefix(apiURL)})
      void queryClient.invalidateQueries({queryKey: chatSidebarQueryKey(apiURL)})
      if (apiURL) {
        void queryClient.invalidateQueries({queryKey: chatSessionQueryKey(apiURL, taskId)})
        void queryClient.invalidateQueries({queryKey: ['quick-chat-task-bootstrap', apiURL, taskId]})
      }
    },
    [apiURL, queryClient],
  )

  const chats = useMemo(
    () =>
      [...tasks]
        .sort((a, b) => getTaskTimestamp(b) - getTaskTimestamp(a))
        .map(task => ({
          taskId: task.taskId,
          creatorId: task.creatorId,
          agentSlug: task.agentSlug,
          customAgentName: task.customAgentName,
          nwo: task.nwo,
          sharingStatus: task.sharingStatus,
          archivedAt: task.archivedAt,
          title: getTaskTitle(task),
          updatedAt: task.updatedAt,
          latestSessionState: task.latestSessionState,
          latestSessionId: task.latestSessionId,
          resumable: task.resumable,
          resumeBlocker: task.resumeBlocker,
          environment: task.currentEnvironment ?? undefined,
        })),
    [tasks],
  )

  const previousStateRef = useRef<
    {isLoading: boolean; isError: boolean; isSuccess: boolean; isLoadMoreError: boolean; count: number} | undefined
  >(undefined)
  // Announce query state transitions once from the layout owner rather than from
  // each responsive sidebar copy.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- synchronize the external live-region announcer with query transitions.
    if (!queryEnabled) {
      previousStateRef.current = undefined
      return
    }

    const state = {
      isLoading: query.isLoading,
      isError: hasLoadError,
      isSuccess: query.isSuccess,
      isLoadMoreError: hasFetchNextPageError,
      count: chats.length,
    }
    const previous = previousStateRef.current
    const announceResult: typeof announce = announceResults ? announce : () => {}

    if (state.isLoading && !previous?.isLoading) {
      announce('Loading chats.')
    } else if (state.isError && !previous?.isError) {
      announceResult('Chats could not be loaded.', {assertive: true})
    } else if (state.isLoadMoreError && !previous?.isLoadMoreError) {
      announce('More chats could not be loaded.', {assertive: true})
    } else if (state.isSuccess && !previous?.isSuccess) {
      announceResult(`${chats.length} chat${chats.length === 1 ? '' : 's'} loaded.`)
    } else if (state.isSuccess && chats.length > (previous?.count ?? 0)) {
      // "Show more..." appended an older page; rows arrive silently otherwise.
      announceResult(`${chats.length - (previous?.count ?? 0)} more chats loaded.`)
    }

    previousStateRef.current = state
  }, [
    announceResults,
    chats.length,
    hasFetchNextPageError,
    hasLoadError,
    query.isLoading,
    query.isSuccess,
    queryEnabled,
  ])

  return {
    chats,
    isLoadingChats: query.isLoading && !query.data,
    isErrorChats: hasLoadError,
    hasMoreChats: hasNextPage,
    isLoadingMoreChats: isFetchingNextPage,
    hasLoadMoreError: hasFetchNextPageError,
    loadMoreChats,
    apiURL,
    onTaskStateChange,
  }
}
